import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { 
  LogOut, ShoppingCart, Plus, Minus, Trash2, Search,
  DollarSign, CreditCard, Banknote, Package, Receipt, FileText,
  Check, X, ArrowLeft, ClipboardList, Printer, Eye,
  MessageSquare, Bell, Headphones, Camera, Link2, Wifi, UtensilsCrossed, Users
} from 'lucide-react';
import { toast } from 'sonner';
import { escapeHtml } from '../../utils/security';
import { playNotificationSound } from '../../utils/notificationSound';
import SupportChat from '../../components/admin/SupportChat';
import BarcodeScanner from '../../components/BarcodeScanner';
import PosSyncModal from '../../components/admin/PosSyncModal';
import TableMode from '../../components/admin/TableMode';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PAYMENT_LABELS = { cash: 'Efectivo', card: 'Tarjeta', transfer: 'Transferencia' };
const UNIT_SHORT = { unidad: 'ud', libra: 'lb', kilo: 'kg', gramo: 'g' };
const isWeightUnit = (u) => u === 'libra' || u === 'kilo' || u === 'gramo';

const PointOfSale = () => {
  const { storeDomain, adminPath } = useParams();
  const { user, logout, getAuthHeader, getAdminBasePath, hasPermission, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const basePath = adminPath ? `/${adminPath}` : (storeDomain ? `/admin/${storeDomain}` : getAdminBasePath());
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('');
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('cash');
  const [cashierName, setCashierName] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [todaySales, setTodaySales] = useState({ total: 0, count: 0 });
  const [showCashClose, setShowCashClose] = useState(false);
  const [cashCloseData, setCashCloseData] = useState(null);
  const [loadingCashClose, setLoadingCashClose] = useState(false);
  const [cashCloseDate, setCashCloseDate] = useState('');
  // New states
  const [showDaySales, setShowDaySales] = useState(false);
  const [daySalesData, setDaySalesData] = useState([]);
  const [loadingDaySales, setLoadingDaySales] = useState(false);
  const [lastSale, setLastSale] = useState(null);
  const [showReceipt, setShowReceipt] = useState(false);
  const [storeName, setStoreName] = useState('');
  const [storeIvaRegime, setStoreIvaRegime] = useState('iva_0');
  // Notifications
  const [notifications, setNotifications] = useState({ unread_chats: 0, pending_orders: 0, total: 0 });
  const [supportUnread, setSupportUnread] = useState(0);
  const [showSupportChat, setShowSupportChat] = useState(false);
  const prevNotifRef = React.useRef({ chats: 0, orders: 0, support: 0 });
  const [showScanner, setShowScanner] = useState(false);
  // Sync state
  const [showSyncModal, setShowSyncModal] = useState(false);
  const [syncCode, setSyncCode] = useState(null);
  const [syncDevices, setSyncDevices] = useState(0);
  const wsRef = React.useRef(null);
  // Mode: 'pos' or 'mesas'
  const [posMode, setPosMode] = useState('pos');
  const [mobileCartOpen, setMobileCartOpen] = useState(false);
  // Customer for sale
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerResults, setCustomerResults] = useState([]);
  const [showCustomerSearch, setShowCustomerSearch] = useState(false);
  const [showQuickCustomer, setShowQuickCustomer] = useState(false);
  const [quickCustomerName, setQuickCustomerName] = useState('');
  const [quickCustomerPhone, setQuickCustomerPhone] = useState('');

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate(adminPath ? `/${adminPath}/login` : (storeDomain ? `/admin/${storeDomain}/login` : '/admin/login'));
      return;
    }
    if ((adminPath && user.admin_path && adminPath !== user.admin_path) || (storeDomain && user.store_domain && storeDomain !== user.store_domain)) {
      logout(); navigate(adminPath ? `/${adminPath}/login` : `/admin/${storeDomain}/login`, { replace: true }); return;
    }
    setCashierName(user.username);
    loadData();
    loadStoreName();
    loadNotifications();
    const notifInterval = setInterval(loadNotifications, 8000);
    return () => clearInterval(notifInterval);
  }, [user, navigate, authLoading, storeDomain, adminPath]);

  const loadNotifications = async () => {
    try {
      const [notifRes, supportRes] = await Promise.all([
        axios.get(`${API}/admin/notifications`, { headers: getAuthHeader() }),
        axios.get(`${API}/admin/support-unread`, { headers: getAuthHeader() })
      ]);
      const n = notifRes.data;
      const su = supportRes.data.unread || 0;
      if (n.unread_chats > prevNotifRef.current.chats && prevNotifRef.current.chats >= 0) {
        playNotificationSound('message');
        toast.info(`Nuevo mensaje de cliente`);
      }
      if (n.pending_orders > prevNotifRef.current.orders && prevNotifRef.current.orders >= 0) {
        playNotificationSound('order');
        toast.info(`Nuevo pedido recibido`);
      }
      if (su > prevNotifRef.current.support && prevNotifRef.current.support >= 0) {
        playNotificationSound('message');
        toast.info(`Mensaje de Soporte Vendix`);
      }
      prevNotifRef.current = { chats: n.unread_chats, orders: n.pending_orders, support: su };
      setNotifications(n);
      setSupportUnread(su);
    } catch (e) { /* ignore */ }
  };

  const loadStoreName = async () => {
    try {
      const storeId = user?.store_id || localStorage.getItem('admin_store_id');
      const res = await axios.get(`${API}/settings${storeId ? `?store_id=${storeId}` : ''}`, { headers: getAuthHeader() });
      setStoreName(res.data?.store_name || 'Mi Tienda');
      setStoreIvaRegime(res.data?.iva_regime || 'iva_0');
    } catch (e) { setStoreName('Mi Tienda'); }
  };

  const loadData = async () => {
    try {
      const storeId = user?.store_id || localStorage.getItem('admin_store_id');
      const [productsRes, categoriesRes, salesRes] = await Promise.all([
        axios.get(`${API}/products?store_id=${storeId}`),
        axios.get(`${API}/categories?store_id=${storeId}`),
        axios.get(`${API}/sales/today`, { headers: getAuthHeader() })
      ]);
      setProducts(productsRes.data);
      setCategories(categoriesRes.data);
      setTodaySales({
        total: salesRes.data.total_revenue,
        count: salesRes.data.total_sales,
        profit: salesRes.data.total_profit
      });
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products.filter(p => {
    const matchesSearch = p.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = !selectedCategory || p.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const [weightInput, setWeightInput] = useState({ show: false, product: null, value: '' });

  const addToCart = (product) => {
    if (product.stock <= 0) { toast.error('Producto sin stock'); return; }
    const unit = product.unit_type || 'unidad';
    if (isWeightUnit(unit)) {
      setWeightInput({ show: true, product, value: '' });
      return;
    }
    addUnitToCart(product, 1);
  };

  const handleBarcodeScan = async (barcode) => {
    try {
      const res = await axios.get(`${API}/products/barcode/${barcode}`, { headers: getAuthHeader() });
      const product = res.data;
      addToCart(product);
      playNotificationSound('message');
      toast.success(`${product.name} agregado`);
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error(`No se encontró producto con código: ${barcode}`);
      } else {
        toast.error('Error buscando producto');
      }
    }
  };

  // === POS Sync ===
  const connectSync = (code) => {
    setSyncCode(code);
    setShowSyncModal(false);

    const wsProtocol = BACKEND_URL.startsWith('https') ? 'wss' : 'ws';
    const wsHost = BACKEND_URL.replace(/^https?:\/\//, '');
    const wsUrl = `${wsProtocol}://${wsHost}/api/ws/pos-sync/${code}`;

    const ws = new WebSocket(wsUrl);
    wsRef.current = ws;

    ws.onopen = () => {
      toast.success(`Sincronización activa - Código: ${code}`);
    };

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);
        if (data.type === 'devices') {
          setSyncDevices(data.count);
        } else if (data.type === 'add_product') {
          // Remote device added a product
          const product = data.product;
          const qty = data.qty || 1;
          setCart(prev => {
            const existing = prev.find(item => item.product_id === product.id);
            if (existing) {
              return prev.map(item =>
                item.product_id === product.id
                  ? { ...item, quantity: item.quantity + qty, subtotal: (item.quantity + qty) * item.unit_price }
                  : item
              );
            } else {
              return [...prev, {
                product_id: product.id, product_name: product.name,
                quantity: qty, unit_price: product.price, cost_price: product.cost_price || 0,
                subtotal: qty * product.price, stock: product.stock,
                unit_type: product.unit_type || 'unidad', tax_rate: product.tax_rate || 0
              }];
            }
          });
          playNotificationSound('order');
          toast.success(`${product.name} (remoto)`);
        } else if (data.type === 'update_qty') {
          setCart(prev => prev.map(item => {
            if (item.product_id === data.productId) {
              const newQty = item.quantity + data.delta;
              if (newQty <= 0) return null;
              return { ...item, quantity: newQty, subtotal: newQty * item.unit_price };
            }
            return item;
          }).filter(Boolean));
        } else if (data.type === 'set_weight') {
          const qty = parseFloat(data.value);
          if (!isNaN(qty) && qty > 0) {
            setCart(prev => prev.map(item =>
              item.product_id === data.productId
                ? { ...item, quantity: qty, subtotal: qty * item.unit_price }
                : item
            ));
          }
        } else if (data.type === 'remove') {
          setCart(prev => prev.filter(item => item.product_id !== data.productId));
        } else if (data.type === 'clear_cart') {
          setCart([]);
        }
      } catch (e) { console.error('WS parse error', e); }
    };

    ws.onclose = () => {
      if (syncCode) {
        toast.info('Sincronización desconectada');
        setSyncCode(null);
        setSyncDevices(0);
      }
    };

    ws.onerror = () => {
      toast.error('Error de conexión');
    };
  };

  const disconnectSync = () => {
    if (wsRef.current) {
      wsRef.current.close();
      wsRef.current = null;
    }
    setSyncCode(null);
    setSyncDevices(0);
    toast.info('Sincronización desconectada');
  };

  // Cleanup on unmount
  React.useEffect(() => {
    return () => {
      if (wsRef.current) { wsRef.current.close(); wsRef.current = null; }
    };
  }, []);

  const confirmWeightAdd = () => {
    const qty = parseFloat(weightInput.value);
    if (!qty || qty <= 0) { toast.error('Ingresa una cantidad válida'); return; }
    const product = weightInput.product;
    const existing = cart.find(item => item.product_id === product.id);
    const currentQty = existing ? existing.quantity : 0;
    if (currentQty + qty > product.stock) { toast.error('Stock insuficiente'); return; }
    addUnitToCart(product, qty);
    setWeightInput({ show: false, product: null, value: '' });
  };

  // === Sync helper ===
  const sendSync = (action) => {
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(action));
    }
  };

  const addUnitToCart = (product, qty) => {
    setCart(prev => {
      const existing = prev.find(item => item.product_id === product.id);
      if (existing) {
        if (existing.quantity + qty > product.stock) { toast.error('Stock insuficiente'); return prev; }
        return prev.map(item =>
          item.product_id === product.id
            ? { ...item, quantity: item.quantity + qty, subtotal: (item.quantity + qty) * item.unit_price }
            : item
        );
      } else {
        return [...prev, {
          product_id: product.id, product_name: product.name,
          quantity: qty, unit_price: product.price, cost_price: product.cost_price || 0,
          subtotal: qty * product.price, stock: product.stock,
          unit_type: product.unit_type || 'unidad', tax_rate: product.tax_rate || 0
        }];
      }
    });
    sendSync({ type: 'add_product', product, qty });
  };

  const updateQuantity = (productId, delta) => {
    setCart(prev => prev.map(item => {
      if (item.product_id === productId) {
        const newQty = item.quantity + delta;
        if (newQty <= 0) return null;
        if (newQty > item.stock) { toast.error('Stock insuficiente'); return item; }
        return { ...item, quantity: newQty, subtotal: newQty * item.unit_price };
      }
      return item;
    }).filter(Boolean));
    sendSync({ type: 'update_qty', productId, delta });
  };

  const setWeightQuantity = (productId, value) => {
    const qty = parseFloat(value);
    if (isNaN(qty) || qty <= 0) return;
    setCart(prev => prev.map(item => {
      if (item.product_id === productId) {
        if (qty > item.stock) { toast.error('Stock insuficiente'); return item; }
        return { ...item, quantity: qty, subtotal: qty * item.unit_price };
      }
      return item;
    }));
    sendSync({ type: 'set_weight', productId, value });
  };

  const removeFromCart = (productId) => {
    setCart(prev => prev.filter(item => item.product_id !== productId));
    sendSync({ type: 'remove', productId });
  };
  const cartTotal = useMemo(() => cart.reduce((sum, item) => sum + item.subtotal, 0), [cart]);

  const handleSale = () => {
    if (cart.length === 0) { toast.error('Agrega productos al carrito'); return; }
    setShowConfirm(true);
  };

  const confirmSale = async () => {
    setProcessing(true);
    try {
      const saleData = {
        items: cart.map(item => ({
          product_id: item.product_id, product_name: item.product_name,
          quantity: item.quantity, unit_price: item.unit_price,
          cost_price: item.cost_price, subtotal: item.subtotal,
          tax_rate: item.tax_rate || 0
        })),
        total: cartTotal, payment_method: paymentMethod, cashier_name: cashierName,
        customer_id: selectedCustomer?.id || null,
        customer_name: selectedCustomer?.name || null
      };
      const saleRes = await axios.post(`${API}/sales`, saleData, { headers: getAuthHeader() });
      
      let accessKey = '';
      let facturaId = '';
      // If electronic invoice selected, send to Factuplan
      if (invoiceType === 'invoice') {
        setProcessingInvoice(true);
        try {
          const invRes = await axios.post(`${API}/factuplan/invoice`, {
            sale_id: saleRes.data?.sale_id || saleRes.data?.id || '',
            items: saleData.items,
            total: cartTotal,
            payment_method: paymentMethod,
            customer: {
              name: invoiceCustomer.name || selectedCustomer?.name || 'Consumidor Final',
              identification: invoiceCustomer.identification || '9999999999999',
              id_type: invoiceCustomer.id_type || 'FINAL_CONSUMER',
              email: invoiceCustomer.email || ''
            }
          }, { headers: getAuthHeader() });
          
          if (invRes.data.success) {
            accessKey = invRes.data.access_key || '';
            facturaId = invRes.data.factuplan_id || '';
            toast.success('Factura electrónica autorizada');
            // Save customer for auto-fill
            if (invoiceCustomer.identification && invoiceCustomer.identification !== '9999999999999') {
              axios.post(`${API}/invoice-customers`, {
                identification: invoiceCustomer.identification, name: invoiceCustomer.name,
                email: invoiceCustomer.email, id_type: invoiceCustomer.id_type
              }, { headers: getAuthHeader() }).catch(() => {});
            }
          } else {
            toast.error(invRes.data.message || 'Error en facturación');
          }
        } catch (e) {
          toast.error('Error al generar factura electrónica');
        } finally {
          setProcessingInvoice(false);
        }
      }
      
      setLastSale({
        items: [...cart], total: cartTotal, payment_method: paymentMethod,
        cashier_name: cashierName, timestamp: new Date(),
        customer_name: invoiceType === 'invoice' ? (invoiceCustomer.name || selectedCustomer?.name) : (selectedCustomer?.name || null),
        access_key: accessKey,
        factuplan_id: facturaId,
        invoice_type: invoiceType,
        customer_id_number: invoiceType === 'invoice' ? invoiceCustomer.identification : '',
        customer_id_type: invoiceType === 'invoice' ? invoiceCustomer.id_type : ''
      });
      
      toast.success(`Venta registrada: $${cartTotal.toFixed(2)}`);
      setCart([]);
      setShowConfirm(false);
      setShowReceipt(true);
      setSelectedCustomer(null);
      setCustomerSearch('');
      setInvoiceType('ticket');
      setInvoiceCustomer({ name: '', identification: '', id_type: 'FINAL_CONSUMER', email: '' });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al registrar venta');
    } finally {
      setProcessing(false);
    }
  };

  const searchCustomers = async (q) => {
    setCustomerSearch(q);
    if (q.length < 2) { setCustomerResults([]); return; }
    try {
      const res = await axios.get(`${API}/customers?search=${encodeURIComponent(q)}`, { headers: getAuthHeader() });
      setCustomerResults(res.data);
    } catch { setCustomerResults([]); }
  };

  const createQuickCustomer = async () => {
    if (!quickCustomerName.trim()) { toast.error('Nombre obligatorio'); return; }
    try {
      const res = await axios.post(`${API}/customers`, {
        name: quickCustomerName.trim(),
        phone: quickCustomerPhone.trim()
      }, { headers: getAuthHeader() });
      setSelectedCustomer(res.data);
      setShowQuickCustomer(false);
      setQuickCustomerName('');
      setQuickCustomerPhone('');
      toast.success(`Cliente ${res.data.name} creado`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Error'); }
  };

  const handleLogout = () => {
    logout();
    navigate(adminPath ? `/${adminPath}/login` : (storeDomain ? `/admin/${storeDomain}/login` : '/admin/login'));
  };

  const handleCashClose = async (dateParam) => {
    setLoadingCashClose(true);
    try {
      const url = dateParam ? `${API}/sales/cashclose?date=${dateParam}` : `${API}/sales/cashclose`;
      const response = await axios.get(url, { headers: getAuthHeader() });
      setCashCloseData(response.data);
      setShowCashClose(true);
    } catch (error) {
      toast.error('Error al cargar cierre de caja');
    } finally {
      setLoadingCashClose(false);
    }
  };

  const loadDaySales = async () => {
    setLoadingDaySales(true);
    try {
      const res = await axios.get(`${API}/sales/cashclose`, { headers: getAuthHeader() });
      setDaySalesData(res.data?.sales || []);
      setShowDaySales(true);
    } catch (error) {
      toast.error('Error al cargar ventas del día');
    } finally {
      setLoadingDaySales(false);
    }
  };

  const printReceipt = (sale) => {
    const w = window.open('', '_blank', 'width=350,height=700');
    if (!w) { toast.error('Permite ventanas emergentes para imprimir'); return; }
    const items = sale.items || [];
    const itemsHtml = items.map(i => {
      const qty = Number(i.quantity) || 0;
      const price = Number(i.unit_price) || 0;
      const sub = Number(i.subtotal) || (qty * price);
      return `
      <tr>
        <td style="text-align:left;padding:4px 0">${escapeHtml(i.product_name)}</td>
        <td style="text-align:center;padding:4px 0">${qty}</td>
        <td style="text-align:right;padding:4px 0">$${price.toFixed(2)}</td>
        <td style="text-align:right;padding:4px 0;font-weight:bold">$${sub.toFixed(2)}</td>
      </tr>`;
    }).join('');
    const date = new Date(sale.timestamp || sale.created_at || Date.now());
    const sName = escapeHtml(storeName);
    const cashier = escapeHtml(sale.cashier_name || cashierName);
    const payLabel = escapeHtml(PAYMENT_LABELS[sale.payment_method] || sale.payment_method || '');
    const total = Number(sale.total) || 0;
    const accessKey = sale.access_key || '';
    const isInvoice = sale.invoice_type === 'invoice';
    const customerName = sale.customer_name || '';
    const customerIdNum = sale.customer_id_number || '';
    const idTypeLabel = sale.customer_id_type === 'RUC' ? 'RUC' : sale.customer_id_type === 'CEDULA' ? 'Cédula' : 'ID';
    // Calculate IVA breakdown based on store's iva_regime
    let subtotalBase0 = 0, subtotalBaseIVA = 0, totalIVA = 0;
    items.forEach(i => {
      const qty = Number(i.quantity) || 0;
      const pvp = Number(i.unit_price) || 0;
      if (storeIvaRegime === 'iva_15') {
        const base = pvp / 1.15;
        subtotalBaseIVA += base * qty;
        totalIVA += (pvp - base) * qty;
      } else {
        subtotalBase0 += pvp * qty;
      }
    });
    const subtotalSinIVA = subtotalBase0 + subtotalBaseIVA;
    w.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Recibo</title>
      <style>
        @page { margin: 0; size: 80mm auto; }
        * { margin: 0; padding: 0; box-sizing: border-box; }
        body {
          font-family: 'Arial Black', 'Arial', 'Helvetica', sans-serif;
          width: 76mm; margin: 2mm auto; padding: 0;
          color: #000; font-size: 14px; font-weight: 500;
          -webkit-print-color-adjust: exact; print-color-adjust: exact;
        }
        .header { text-align: center; padding-bottom: 6px; }
        .store-name { font-size: 22px; font-weight: 900; letter-spacing: 1px; margin-bottom: 4px; text-transform: uppercase; }
        .info { font-size: 13px; color: #000; line-height: 1.6; font-weight: 600; }
        .sep { border: none; border-top: 2px dashed #000; margin: 6px 0; }
        .sep-thick { border: none; border-top: 3px solid #000; margin: 8px 0; }
        table { width: 100%; border-collapse: collapse; font-size: 13px; }
        th { font-size: 12px; font-weight: 900; text-transform: uppercase; padding: 4px 0; border-bottom: 2px solid #000; }
        td { font-weight: 600; }
        .total-line td { font-size: 24px; font-weight: 900; padding: 6px 0; }
        .pay-line { text-align: center; font-size: 14px; font-weight: 700; margin: 6px 0; padding: 4px 0; border: 1px solid #000; }
        .footer { text-align: center; font-size: 12px; color: #000; margin-top: 8px; line-height: 1.6; font-weight: 600; }
        .access-key { font-size: 9px; word-break: break-all; text-align: center; font-weight: 600; margin: 4px 0; font-family: monospace; letter-spacing: 0.5px; }
        .sri-note { text-align: center; font-size: 10px; font-weight: 600; margin: 4px 0; }
      </style>
    </head><body>
      <div class="header">
        <div class="store-name">${sName}</div>
        ${isInvoice ? '<div class="info" style="font-size:11px;font-weight:900;border:1px solid #000;padding:2px;margin:4px 0">FACTURA ELECTRONICA</div>' : ''}
        <div class="info">${date.toLocaleDateString('es-EC', {day:'2-digit',month:'short',year:'numeric'})} - ${date.toLocaleTimeString('es-EC', {hour:'2-digit',minute:'2-digit'})}</div>
        <div class="info">Cajero: ${cashier}</div>
        ${isInvoice && customerName ? `<div class="info">Cliente: ${escapeHtml(customerName)}</div>` : ''}
        ${isInvoice && customerIdNum ? `<div class="info">${idTypeLabel}: ${escapeHtml(customerIdNum)}</div>` : ''}
        ${!isInvoice ? '<div class="info" style="font-size:11px">CONSUMIDOR FINAL</div>' : ''}
      </div>
      <hr class="sep">
      <table>
        <thead><tr>
          <th style="text-align:left">Producto</th>
          <th style="text-align:center">Cant</th>
          <th style="text-align:right">P.U.</th>
          <th style="text-align:right">Subt.</th>
        </tr></thead>
        <tbody>${itemsHtml}</tbody>
      </table>
      <hr class="sep">
      <table>
        ${isInvoice ? `
          <tr><td>Subtotal (sin IVA):</td><td style="text-align:right">$${subtotalSinIVA.toFixed(2)}</td></tr>
          ${subtotalBase0 > 0 ? `<tr><td style="font-size:11px">Base IVA 0%:</td><td style="text-align:right;font-size:11px">$${subtotalBase0.toFixed(2)}</td></tr>` : ''}
          ${subtotalBaseIVA > 0 ? `<tr><td style="font-size:11px">Base IVA 15%:</td><td style="text-align:right;font-size:11px">$${subtotalBaseIVA.toFixed(2)}</td></tr>` : ''}
          <tr><td>IVA 15%:</td><td style="text-align:right">$${totalIVA.toFixed(2)}</td></tr>
        ` : `
          <tr><td>Subtotal:</td><td style="text-align:right">$${total.toFixed(2)}</td></tr>
        `}
      </table>
      <hr class="sep-thick">
      <table><tr class="total-line">
        <td>TOTAL</td>
        <td style="text-align:right">$${total.toFixed(2)}</td>
      </tr></table>
      <div class="pay-line">Pago: ${payLabel}</div>
      ${isInvoice && accessKey ? `
        <hr class="sep">
        <div class="sri-note">CLAVE DE ACCESO:</div>
        <div class="access-key">${escapeHtml(accessKey)}</div>
        <div class="sri-note">Consulte en: https://cel.sri.gob.ec/</div>
      ` : ''}
      <hr class="sep">
      <div class="footer">
        Gracias por su compra<br>
        <strong>${sName}</strong>
      </div>
      <script>window.onload=function(){window.print();}<\/script>
    </body></html>`);
    w.document.close();
  };

  const [voidingId, setVoidingId] = useState(null);
  const [invoiceType, setInvoiceType] = useState('ticket'); // 'ticket' | 'invoice'
  const [invoiceCustomer, setInvoiceCustomer] = useState({ name: '', identification: '', id_type: 'FINAL_CONSUMER', email: '' });
  const [processingInvoice, setProcessingInvoice] = useState(false);

  const handleVoidSale = async (saleId, total) => {
    if (!window.confirm(`¿Está seguro de ANULAR esta venta de $${total.toFixed(2)}?\n\nEsta acción:\n• Restaurará el inventario\n• Eliminará la venta de los reportes\n• Quedará registrada en auditoría`)) return;
    setVoidingId(saleId);
    try {
      await axios.delete(`${API}/sales/${saleId}`, { headers: getAuthHeader() });
      toast.success('Venta anulada. Stock restaurado.');
      setDaySalesData(prev => prev.filter(s => s.id !== saleId));
      // Refresh today's totals
      try {
        const salesRes = await axios.get(`${API}/sales/today`, { headers: getAuthHeader() });
        setTodaySales({ total: salesRes.data.total_revenue, count: salesRes.data.total_sales, profit: salesRes.data.total_profit });
      } catch {}
    } catch (e) { toast.error(e.response?.data?.detail || 'Error al anular venta'); }
    finally { setVoidingId(null); }
  };

  const printCashClose = (data) => {
    const w = window.open('', '_blank', 'width=350,height=800');
    if (!w) { toast.error('Permite ventanas emergentes para imprimir'); return; }
    const date = new Date();
    const sName = escapeHtml(storeName || 'Tienda');
    const productsHtml = (data.products_sold || []).map(p => `
      <tr>
        <td style="text-align:left;padding:3px 0">${escapeHtml(p.product_name)}</td>
        <td style="text-align:center;padding:3px 0;font-weight:bold">${p.quantity}</td>
        <td style="text-align:right;padding:3px 0;font-weight:bold">$${p.subtotal.toFixed(2)}</td>
      </tr>`).join('');
    const byPayment = data.by_payment_method || {};
    w.document.write(`<!DOCTYPE html><html><head><meta charset="UTF-8"><title>Cierre de Caja</title>
      <style>
        @page{margin:0;size:80mm auto}*{margin:0;padding:0;box-sizing:border-box}
        body{font-family:'Arial Black','Arial',sans-serif;width:76mm;margin:2mm auto;color:#000;font-size:13px;font-weight:500}
        .header{text-align:center;padding-bottom:6px}
        .store-name{font-size:20px;font-weight:900;letter-spacing:1px;margin-bottom:2px;text-transform:uppercase}
        .title{font-size:16px;font-weight:900;margin:4px 0;border:2px solid #000;padding:4px;text-align:center}
        .info{font-size:12px;line-height:1.5;font-weight:600}
        .sep{border:none;border-top:2px dashed #000;margin:6px 0}
        .sep-thick{border:none;border-top:3px solid #000;margin:8px 0}
        table{width:100%;border-collapse:collapse}
        th{font-size:11px;font-weight:900;text-transform:uppercase;padding:3px 0;border-bottom:2px solid #000}
        td{font-weight:600;font-size:12px}
        .total-row{font-size:20px;font-weight:900}
        .summary td{padding:3px 0;font-size:13px}
        .footer{text-align:center;font-size:11px;margin-top:8px;font-weight:600}
      </style></head><body>
      <div class="header">
        <div class="store-name">${sName}</div>
        <div class="title">CIERRE DE CAJA</div>
        <div class="info">${date.toLocaleDateString('es-EC',{weekday:'long',day:'2-digit',month:'long',year:'numeric'})}</div>
        <div class="info">${date.toLocaleTimeString('es-EC',{hour:'2-digit',minute:'2-digit'})}</div>
        <div class="info">Cajero: ${escapeHtml(cashierName)}</div>
      </div>
      <hr class="sep-thick">
      <table class="summary">
        <tr><td>Total Ventas:</td><td style="text-align:right;font-weight:900">${data.total_sales}</td></tr>
        <tr><td>Ingresos:</td><td style="text-align:right;font-weight:900">$${data.total_revenue.toFixed(2)}</td></tr>
        <tr><td>Costo:</td><td style="text-align:right">$${data.total_cost.toFixed(2)}</td></tr>
        <tr><td>Ganancia:</td><td style="text-align:right;font-weight:900">$${data.total_profit.toFixed(2)}</td></tr>
      </table>
      <hr class="sep">
      <div style="font-size:13px;font-weight:900;margin-bottom:4px">METODO DE PAGO</div>
      <table class="summary">
        <tr><td>Efectivo:</td><td style="text-align:right">$${(byPayment.cash?.total||0).toFixed(2)} (${byPayment.cash?.count||0})</td></tr>
        <tr><td>Tarjeta:</td><td style="text-align:right">$${(byPayment.card?.total||0).toFixed(2)} (${byPayment.card?.count||0})</td></tr>
        <tr><td>Transferencia:</td><td style="text-align:right">$${(byPayment.transfer?.total||0).toFixed(2)} (${byPayment.transfer?.count||0})</td></tr>
      </table>
      ${productsHtml ? `
        <hr class="sep">
        <div style="font-size:13px;font-weight:900;margin-bottom:4px">PRODUCTOS VENDIDOS</div>
        <table>
          <thead><tr><th style="text-align:left">Producto</th><th style="text-align:center">Cant</th><th style="text-align:right">Total</th></tr></thead>
          <tbody>${productsHtml}</tbody>
        </table>
      ` : ''}
      <hr class="sep-thick">
      ${data.iva_regime === 'iva_15' ? `
        <div style="font-size:13px;font-weight:900;margin-bottom:4px">DESGLOSE IVA</div>
        <table class="summary">
          <tr><td>Subtotal (Base 15%):</td><td style="text-align:right">$${(data.subtotal_base_iva || 0).toFixed(2)}</td></tr>
          <tr><td>IVA 15%:</td><td style="text-align:right;font-weight:900">$${(data.total_iva || 0).toFixed(2)}</td></tr>
        </table>
        <hr class="sep">
      ` : `
        <table class="summary">
          <tr><td>Base IVA 0%:</td><td style="text-align:right">$${data.total_revenue.toFixed(2)}</td></tr>
        </table>
        <hr class="sep">
      `}
      <table><tr>
        <td class="total-row">TOTAL</td>
        <td class="total-row" style="text-align:right">$${data.total_revenue.toFixed(2)}</td>
      </tr></table>
      <hr class="sep">
      <div class="footer">${sName}<br>Cierre de caja - ${date.toLocaleDateString('es-EC')}</div>
      <script>window.onload=function(){window.print();}<\/script>
    </body></html>`);
    w.document.close();
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: 'var(--bg-section)' }}>
        <ShoppingCart size={48} className="animate-spin text-accent-text" />
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ background: 'var(--bg-section)', overflowX: 'hidden' }}>
      {/* Header */}
      <div className="bg-white border-b" style={{ borderColor: 'var(--border-light)' }}>
        <div className="container py-3">
          <div className="flex items-center justify-between gap-2">
            <div className="flex items-center gap-2 min-w-0">
              <button onClick={() => navigate(`${basePath}/dashboard`)} className="p-2 hover:bg-section rounded-lg flex-shrink-0">
                <ArrowLeft size={20} />
              </button>
              <div className="min-w-0">
                <h1 className="font-bold text-base md:text-lg truncate">Punto de Venta</h1>
                <p className="text-xs text-text-muted truncate">Cajero: {cashierName}</p>
              </div>
            </div>
            <button onClick={handleLogout} className="btn-secondary text-sm flex-shrink-0 md:hidden p-2">
              <LogOut size={16} />
            </button>
            <button onClick={handleLogout} className="btn-secondary text-sm flex-shrink-0 hidden md:flex">
              <LogOut size={16} className="mr-1" />
              Salir
            </button>
          </div>
          {/* Mode Toggle + Stats + actions row */}
          <div className="flex gap-2 mt-2 overflow-x-auto pb-1" style={{ scrollbarWidth: 'none' }}>
            {/* Mode Toggle */}
            <div className="flex rounded-lg border overflow-hidden flex-shrink-0" style={{ borderColor: 'var(--border-light)' }} data-testid="pos-mode-toggle">
              <button onClick={() => setPosMode('pos')}
                className="px-3 py-1.5 text-xs font-medium transition-all flex items-center gap-1"
                style={{
                  background: posMode === 'pos' ? 'var(--accent-primary)' : 'white',
                  color: posMode === 'pos' ? 'white' : 'var(--text-muted)'
                }}
                data-testid="mode-pos-btn">
                <ShoppingCart size={14} /> POS
              </button>
              <button onClick={() => setPosMode('mesas')}
                className="px-3 py-1.5 text-xs font-medium transition-all flex items-center gap-1"
                style={{
                  background: posMode === 'mesas' ? 'var(--accent-primary)' : 'white',
                  color: posMode === 'mesas' ? 'white' : 'var(--text-muted)'
                }}
                data-testid="mode-mesas-btn">
                <UtensilsCrossed size={14} /> Mesas
              </button>
            </div>
            <div className="text-center flex-shrink-0 px-1">
              <p className="text-[10px] text-text-muted">Ventas</p>
              <p className="font-bold text-accent-text text-xs">{todaySales.count}</p>
            </div>
            {hasPermission(['admin']) && (
              <div className="text-center flex-shrink-0 px-1 hidden sm:block">
                <p className="text-[10px] text-text-muted">Ingresos</p>
                <p className="font-bold text-green-600 text-xs">${todaySales.total?.toFixed(2)}</p>
              </div>
            )}
            {hasPermission(['admin']) && (
              <div className="text-center flex-shrink-0 px-1 hidden sm:block">
                <p className="text-[10px] text-text-muted">Ganancia</p>
                <p className="font-bold text-blue-600 text-xs">${todaySales.profit?.toFixed(2)}</p>
              </div>
            )}
            {hasPermission(['admin']) && (
              <button onClick={loadDaySales} disabled={loadingDaySales}
                className="px-3 py-1.5 rounded-lg font-medium text-xs border transition-colors hover:bg-accent-wash whitespace-nowrap flex-shrink-0"
                style={{ borderColor: 'var(--accent-primary)', color: 'var(--accent-text)' }}
                data-testid="day-sales-btn">
                <ClipboardList size={14} className="inline mr-1" />
                {loadingDaySales ? '...' : 'Ventas del Día'}
              </button>
            )}
            <button onClick={() => setShowScanner(true)}
              className="px-3 py-1.5 rounded-lg font-medium text-white text-xs transition-colors whitespace-nowrap flex-shrink-0"
              style={{ background: '#10b981' }}
              data-testid="pos-barcode-btn">
              <Camera size={14} className="inline mr-1" />
              Escanear
            </button>
            {syncCode ? (
              <button onClick={disconnectSync}
                className="px-3 py-1.5 rounded-lg font-medium text-xs border transition-colors whitespace-nowrap flex-shrink-0 flex items-center gap-1"
                style={{ borderColor: '#10b981', color: '#10b981', background: '#ecfdf5' }}
                data-testid="pos-sync-connected">
                <Wifi size={14} />
                <span className="hidden sm:inline">Sync</span> {syncCode}
                {syncDevices > 1 && <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />}
              </button>
            ) : (
              <button onClick={() => setShowSyncModal(true)}
                className="px-3 py-1.5 rounded-lg font-medium text-xs border transition-colors hover:bg-green-50 whitespace-nowrap flex-shrink-0"
                style={{ borderColor: '#10b981', color: '#10b981' }}
                data-testid="pos-sync-btn">
                <Link2 size={14} className="inline mr-1" />
                Sincronizar
              </button>
            )}
            {hasPermission(['admin']) && (
              <button onClick={() => { setCashCloseDate(''); handleCashClose(); }} disabled={loadingCashClose}
                className="px-3 py-1.5 rounded-lg font-medium text-white text-xs transition-colors whitespace-nowrap flex-shrink-0"
                style={{ background: '#dc2626' }} data-testid="cash-close-btn">
                <Receipt size={14} className="inline mr-1" />
                {loadingCashClose ? '...' : 'Cierre de Caja'}
              </button>
            )}
            <button onClick={() => navigate(`${basePath}/orders`)}
              className="px-3 py-1.5 rounded-lg font-medium text-xs border transition-colors hover:bg-orange-50 whitespace-nowrap flex-shrink-0 relative"
              style={{ borderColor: '#f97316', color: '#f97316' }}
              data-testid="pos-orders-btn">
              <Package size={14} className="inline mr-1" />
              Pedidos
              {notifications.pending_orders > 0 && (
                <span className="absolute -top-2 -right-2 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">{notifications.pending_orders}</span>
              )}
            </button>
            <button onClick={() => navigate(`${basePath}/chat`)}
              className="px-3 py-1.5 rounded-lg font-medium text-xs border transition-colors hover:bg-blue-50 whitespace-nowrap flex-shrink-0 relative"
              style={{ borderColor: '#3b82f6', color: '#3b82f6' }}
              data-testid="pos-chat-btn">
              <MessageSquare size={14} className="inline mr-1" />
              Mensajes
              {notifications.unread_chats > 0 && (
                <span className="absolute -top-2 -right-2 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">{notifications.unread_chats}</span>
              )}
            </button>
            <button onClick={() => setShowSupportChat(true)}
              className="px-3 py-1.5 rounded-lg font-medium text-xs border transition-colors hover:bg-purple-50 whitespace-nowrap flex-shrink-0 relative"
              style={{ borderColor: '#6366f1', color: '#6366f1' }}
              data-testid="pos-support-btn">
              <Headphones size={14} className="inline mr-1" />
              Soporte
              {supportUnread > 0 && (
                <span className="absolute -top-2 -right-2 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">{supportUnread}</span>
              )}
            </button>
          </div>
        </div>
      </div>

      <div className="container py-4">
        {posMode === 'mesas' ? (
          <TableMode
            getAuthHeader={getAuthHeader}
            products={products}
            categories={categories}
            cashierName={cashierName}
            storeName={storeName}
            storeIvaRegime={storeIvaRegime}
            onSaleCompleted={loadData}
          />
        ) : (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" style={{ minHeight: 'calc(100vh - 180px)' }}>
          {/* Products Section */}
          <div className="lg:col-span-2 bg-white rounded-xl border overflow-hidden flex flex-col" style={{ borderColor: 'var(--border-light)', paddingBottom: cart.length > 0 ? '80px' : '0' }}>
            <div className="p-3 md:p-4 border-b" style={{ borderColor: 'var(--border-light)' }}>
              <div className="flex gap-2">
                <div className="flex-1 relative">
                  <Search size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-text-muted" />
                  <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                    placeholder="Buscar producto..." className="w-full pl-10 pr-4 py-2 rounded-lg border text-sm"
                    style={{ borderColor: 'var(--border-light)' }} />
                </div>
                <select value={selectedCategory} onChange={(e) => setSelectedCategory(e.target.value)}
                  className="px-2 md:px-4 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border-light)' }}>
                  <option value="">Todas</option>
                  {categories.map(cat => (<option key={cat.id} value={cat.id}>{cat.name}</option>))}
                </select>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-3 md:p-4">
              <div className="grid grid-cols-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2 md:gap-3">
                {filteredProducts.map(product => {
                  const unit = product.unit_type || 'unidad';
                  const unitLabel = UNIT_SHORT[unit] || 'ud';
                  return (
                  <button key={product.id} onClick={() => addToCart(product)} disabled={product.stock <= 0}
                    className={`p-2 md:p-3 rounded-lg border text-left transition-all hover:shadow-md ${product.stock <= 0 ? 'opacity-50 cursor-not-allowed' : 'hover:border-accent-text'}`}
                    style={{ borderColor: 'var(--border-light)' }}>
                    <img src={product.image} alt={product.name} className="w-full h-12 md:h-16 object-cover rounded mb-1" />
                    <p className="text-[11px] md:text-xs font-medium truncate">{product.name}</p>
                    <div className="flex justify-between items-center mt-0.5">
                      <span className="text-xs md:text-sm font-bold text-accent-text">${product.price.toFixed(2)}{isWeightUnit(unit) ? `/${unitLabel}` : ''}</span>
                      <span className={`text-[9px] md:text-xs px-1 rounded ${product.stock > 0 ? 'bg-green-100 text-green-600' : 'bg-red-100 text-red-600'}`}>
                        {isWeightUnit(unit) ? `${product.stock}${unitLabel}` : product.stock}
                      </span>
                    </div>
                  </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Cart Section - hidden on mobile, visible on lg */}
          <div className="hidden lg:flex bg-white rounded-xl border overflow-hidden flex-col" style={{ borderColor: 'var(--border-light)' }}>
            <div className="p-4 border-b" style={{ borderColor: 'var(--border-light)' }}>
              <div className="flex items-center gap-2">
                <ShoppingCart className="text-accent-text" size={20} />
                <h2 className="font-bold">Carrito</h2>
                <span className="ml-auto bg-accent-wash text-accent-text px-2 py-0.5 rounded-full text-sm">{cart.length}</span>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto p-3">
              {cart.length === 0 ? (
                <div className="text-center py-8 text-text-muted">
                  <ShoppingCart size={40} className="mx-auto mb-2 opacity-50" />
                  <p className="text-sm">Carrito vacío</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {cart.map(item => {
                    const unit = item.unit_type || 'unidad';
                    const unitLabel = UNIT_SHORT[unit] || 'ud';
                    const weight = isWeightUnit(unit);
                    return (
                    <div key={item.product_id} className="p-3 rounded-lg border" style={{ borderColor: 'var(--border-light)' }}>
                      <div className="flex justify-between items-start mb-2">
                        <p className="text-sm font-medium flex-1 pr-2">{item.product_name}</p>
                        <button onClick={() => removeFromCart(item.product_id)} className="text-red-500 hover:bg-red-50 p-1 rounded"><Trash2 size={14} /></button>
                      </div>
                      <div className="flex justify-between items-center">
                        {weight ? (
                          <div className="flex items-center gap-1">
                            <input type="number" step="0.01" min="0.01"
                              value={item.quantity}
                              onChange={(e) => setWeightQuantity(item.product_id, e.target.value)}
                              className="w-16 text-center text-sm font-medium px-1 py-1 rounded border"
                              style={{ borderColor: 'var(--accent-primary)' }}
                              data-testid={`weight-input-${item.product_id}`} />
                            <span className="text-xs text-text-muted font-medium">{unitLabel}</span>
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <button onClick={() => updateQuantity(item.product_id, -1)} className="w-7 h-7 rounded-full border flex items-center justify-center hover:bg-section" style={{ borderColor: 'var(--border-light)' }}><Minus size={14} /></button>
                            <span className="w-8 text-center font-medium">{item.quantity}</span>
                            <button onClick={() => updateQuantity(item.product_id, 1)} className="w-7 h-7 rounded-full border flex items-center justify-center hover:bg-section" style={{ borderColor: 'var(--border-light)' }}><Plus size={14} /></button>
                          </div>
                        )}
                        <span className="font-bold text-accent-text">${item.subtotal.toFixed(2)}</span>
                      </div>
                    </div>
                    );
                  })}
                </div>
              )}
            </div>
            <div className="p-4 border-t" style={{ borderColor: 'var(--border-light)' }}>
              <div className="flex gap-2 mb-4">
                {[{key:'cash',icon:Banknote,label:'Efectivo'},{key:'card',icon:CreditCard,label:'Tarjeta'},{key:'transfer',icon:DollarSign,label:'Transf.'}].map(m => (
                  <button key={m.key} onClick={() => setPaymentMethod(m.key)}
                    className={`flex-1 py-2 rounded-lg border flex items-center justify-center gap-1 text-sm ${paymentMethod === m.key ? 'bg-accent-wash border-accent-text text-accent-text' : ''}`}
                    style={{ borderColor: paymentMethod === m.key ? 'var(--accent-primary)' : 'var(--border-light)' }}>
                    <m.icon size={16} />{m.label}
                  </button>
                ))}
              </div>
              <div className="flex justify-between items-center mb-4 p-3 rounded-lg" style={{ background: 'var(--bg-section)' }}>
                <span className="font-medium">TOTAL</span>
                <span className="text-2xl font-bold text-accent-text">${cartTotal.toFixed(2)}</span>
              </div>
              <div className="flex gap-2">
                <button onClick={() => setCart([])} disabled={cart.length === 0} className="btn-secondary flex-1">Limpiar</button>
                <button onClick={handleSale} disabled={cart.length === 0} className="btn-primary flex-1">
                  <Check size={18} className="mr-1" />Cobrar
                </button>
              </div>
              {lastSale && (
                <button onClick={() => printReceipt(lastSale)}
                  className="w-full mt-2 py-2 rounded-lg border text-sm font-medium flex items-center justify-center gap-2 hover:bg-gray-50 transition-colors"
                  style={{ borderColor: 'var(--border-light)' }} data-testid="reprint-last-btn">
                  <Printer size={16} />Reimprimir Último Recibo
                </button>
              )}
            </div>
          </div>
        </div>
        )}

        {/* Mobile Cart Floating Button */}
        {posMode === 'pos' && (
          <button
            onClick={() => setMobileCartOpen(true)}
            className="lg:hidden fixed bottom-4 right-4 z-30 w-14 h-14 rounded-full shadow-lg flex items-center justify-center text-white"
            style={{ background: 'var(--accent-primary)' }}
            data-testid="mobile-cart-btn">
            <ShoppingCart size={24} />
            {cart.length > 0 && (
              <span className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-red-500 text-white text-xs font-bold flex items-center justify-center">{cart.length}</span>
            )}
          </button>
        )}

        {/* Mobile Cart Slide-up Panel */}
        {mobileCartOpen && posMode === 'pos' && (
          <>
            <div className="lg:hidden fixed inset-0 bg-black/50 z-40" onClick={() => setMobileCartOpen(false)} />
            <div className="lg:hidden fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-2xl shadow-2xl" style={{ maxHeight: '85vh' }}>
              <div className="p-4 border-b flex items-center justify-between" style={{ borderColor: 'var(--border-light)' }}>
                <div className="flex items-center gap-2">
                  <ShoppingCart className="text-accent-text" size={20} />
                  <h2 className="font-bold">Carrito</h2>
                  <span className="bg-accent-wash text-accent-text px-2 py-0.5 rounded-full text-sm">{cart.length}</span>
                </div>
                <button onClick={() => setMobileCartOpen(false)} className="p-2 hover:bg-gray-100 rounded-lg"><X size={20} /></button>
              </div>
              <div className="overflow-y-auto p-3" style={{ maxHeight: '40vh' }}>
                {cart.length === 0 ? (
                  <div className="text-center py-6 text-text-muted">
                    <ShoppingCart size={32} className="mx-auto mb-2 opacity-50" />
                    <p className="text-sm">Carrito vacío</p>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {cart.map(item => {
                      const unit = item.unit_type || 'unidad';
                      const unitLabel = UNIT_SHORT[unit] || 'ud';
                      const weight = isWeightUnit(unit);
                      return (
                      <div key={item.product_id} className="p-3 rounded-lg border" style={{ borderColor: 'var(--border-light)' }}>
                        <div className="flex justify-between items-start mb-1">
                          <p className="text-sm font-medium flex-1 pr-2">{item.product_name}</p>
                          <button onClick={() => removeFromCart(item.product_id)} className="text-red-500 hover:bg-red-50 p-1 rounded"><Trash2 size={14} /></button>
                        </div>
                        <div className="flex justify-between items-center">
                          {weight ? (
                            <div className="flex items-center gap-1">
                              <input type="number" step="0.01" min="0.01" value={item.quantity}
                                onChange={(e) => setWeightQuantity(item.product_id, e.target.value)}
                                className="w-16 text-center text-sm font-medium px-1 py-1 rounded border"
                                style={{ borderColor: 'var(--accent-primary)' }} />
                              <span className="text-xs text-text-muted font-medium">{unitLabel}</span>
                            </div>
                          ) : (
                            <div className="flex items-center gap-2">
                              <button onClick={() => updateQuantity(item.product_id, -1)} className="w-7 h-7 rounded-full border flex items-center justify-center hover:bg-section" style={{ borderColor: 'var(--border-light)' }}><Minus size={14} /></button>
                              <span className="w-8 text-center font-medium">{item.quantity}</span>
                              <button onClick={() => updateQuantity(item.product_id, 1)} className="w-7 h-7 rounded-full border flex items-center justify-center hover:bg-section" style={{ borderColor: 'var(--border-light)' }}><Plus size={14} /></button>
                            </div>
                          )}
                          <span className="font-bold text-accent-text">${item.subtotal.toFixed(2)}</span>
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>
              <div className="p-4 border-t" style={{ borderColor: 'var(--border-light)' }}>
                <div className="flex gap-2 mb-3">
                  {[{key:'cash',icon:Banknote,label:'Efectivo'},{key:'card',icon:CreditCard,label:'Tarjeta'},{key:'transfer',icon:DollarSign,label:'Transf.'}].map(m => (
                    <button key={m.key} onClick={() => setPaymentMethod(m.key)}
                      className={`flex-1 py-2 rounded-lg border flex items-center justify-center gap-1 text-xs font-medium ${paymentMethod === m.key ? 'bg-accent-wash border-accent-text text-accent-text' : ''}`}
                      style={{ borderColor: paymentMethod === m.key ? 'var(--accent-primary)' : 'var(--border-light)' }}>
                      <m.icon size={14} />{m.label}
                    </button>
                  ))}
                </div>
                <div className="flex justify-between items-center mb-3 p-3 rounded-lg" style={{ background: 'var(--bg-section)' }}>
                  <span className="font-medium">TOTAL</span>
                  <span className="text-2xl font-bold text-accent-text">${cartTotal.toFixed(2)}</span>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => { setCart([]); setMobileCartOpen(false); }} disabled={cart.length === 0} className="btn-secondary flex-1 text-sm">Limpiar</button>
                  <button onClick={() => { setMobileCartOpen(false); handleSale(); }} disabled={cart.length === 0} className="btn-primary flex-1 text-sm">
                    <Check size={16} className="mr-1" />Cobrar
                  </button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Confirm Sale Modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-md w-full p-6 max-h-[90vh] overflow-y-auto">
            <h3 className="text-xl font-bold mb-4 text-center">Confirmar Venta</h3>
            
            {/* Customer selector */}
            <div className="mb-4 p-3 rounded-lg border" style={{ borderColor: 'var(--border-light)', background: '#fafafa' }}>
              <p className="text-xs font-medium text-text-muted mb-2 flex items-center gap-1"><Users size={14} /> Cliente (opcional)</p>
              {selectedCustomer ? (
                <div className="flex items-center gap-2 p-2 rounded-lg" style={{ background: 'var(--accent-wash)' }}>
                  <div className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ background: 'var(--accent-primary)' }}>
                    {selectedCustomer.name.charAt(0)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm truncate">{selectedCustomer.name}</p>
                    <p className="text-[10px] text-text-muted">{selectedCustomer.phone || selectedCustomer.cedula || ''}</p>
                  </div>
                  <button onClick={() => setSelectedCustomer(null)} className="p-1 hover:bg-white rounded"><X size={14} /></button>
                </div>
              ) : (
                <div>
                  <div className="flex gap-2">
                    <div className="flex-1 relative">
                      <input type="text" value={customerSearch} onChange={e => searchCustomers(e.target.value)}
                        onFocus={() => setShowCustomerSearch(true)}
                        placeholder="Buscar por nombre, tel, cedula..."
                        className="w-full px-3 py-2 rounded-lg border text-sm" style={{ borderColor: 'var(--border-light)' }}
                        data-testid="pos-customer-search" />
                      {showCustomerSearch && customerResults.length > 0 && (
                        <div className="absolute top-full left-0 right-0 bg-white border rounded-lg shadow-lg mt-1 max-h-36 overflow-y-auto z-10" style={{ borderColor: 'var(--border-light)' }}>
                          {customerResults.map(c => (
                            <button key={c.id} onClick={() => { setSelectedCustomer(c); setCustomerSearch(''); setShowCustomerSearch(false); setCustomerResults([]); }}
                              className="w-full flex items-center gap-2 p-2 hover:bg-gray-50 text-left text-sm">
                              <span className="w-6 h-6 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0" style={{ background: 'var(--accent-primary)' }}>{c.name.charAt(0)}</span>
                              <span className="truncate">{c.name}</span>
                              {c.phone && <span className="text-xs text-text-muted ml-auto flex-shrink-0">{c.phone}</span>}
                            </button>
                          ))}
                        </div>
                      )}
                    </div>
                    <button onClick={() => setShowQuickCustomer(true)} className="px-2 py-2 rounded-lg border hover:bg-gray-50" style={{ borderColor: 'var(--border-light)' }} title="Nuevo cliente" data-testid="pos-new-customer-btn">
                      <Plus size={18} className="text-accent-text" />
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="space-y-2 mb-4 max-h-48 overflow-y-auto">
              {cart.map(item => (
                <div key={item.product_id} className="flex justify-between text-sm">
                  <span>{item.quantity}x {item.product_name}</span>
                  <span className="font-medium">${item.subtotal.toFixed(2)}</span>
                </div>
              ))}
            </div>
            <div className="border-t pt-3 mb-4" style={{ borderColor: 'var(--border-light)' }}>
              {/* Invoice type toggle */}
              <div className="mb-3">
                <p className="text-xs font-medium text-text-muted mb-2">Tipo de comprobante:</p>
                <div className="flex rounded-lg border overflow-hidden" style={{ borderColor: 'var(--border-light)' }}>
                  <button onClick={() => setInvoiceType('ticket')}
                    className="flex-1 py-2 text-xs font-medium transition-all flex items-center justify-center gap-1"
                    style={{ background: invoiceType === 'ticket' ? 'var(--accent-primary)' : 'white', color: invoiceType === 'ticket' ? 'white' : 'var(--text-muted)' }}
                    data-testid="invoice-type-ticket">
                    <Receipt size={14} /> Solo Ticket
                  </button>
                  <button onClick={() => setInvoiceType('invoice')}
                    className="flex-1 py-2 text-xs font-medium transition-all flex items-center justify-center gap-1"
                    style={{ background: invoiceType === 'invoice' ? '#16a34a' : 'white', color: invoiceType === 'invoice' ? 'white' : 'var(--text-muted)' }}
                    data-testid="invoice-type-invoice">
                    <FileText size={14} /> Factura Electrónica
                  </button>
                </div>
              </div>
              {/* Invoice customer fields */}
              {invoiceType === 'invoice' && (
                <div className="mb-3 p-3 rounded-lg border space-y-2" style={{ borderColor: '#16a34a40', background: '#f0fdf4' }}>
                  <p className="text-xs font-bold text-green-800">Datos del cliente para factura:</p>
                  <div className="grid grid-cols-2 gap-2">
                    <select value={invoiceCustomer.id_type} onChange={e => setInvoiceCustomer({...invoiceCustomer, id_type: e.target.value})}
                      className="px-2 py-1.5 rounded border text-xs" style={{ borderColor: 'var(--border-light)' }}>
                      <option value="FINAL_CONSUMER">Consumidor Final</option>
                      <option value="CEDULA">Cédula</option>
                      <option value="RUC">RUC</option>
                      <option value="PASSPORT">Pasaporte</option>
                    </select>
                    <input type="text" placeholder="# Identificación" value={invoiceCustomer.identification}
                      onChange={e => {
                        const val = e.target.value;
                        setInvoiceCustomer(prev => ({...prev, identification: val}));
                        if (val.length >= 10 && (val.length === 10 || val.length === 13)) {
                          axios.get(`${API}/invoice-customers/search?identification=${val}`, { headers: getAuthHeader() })
                            .then(r => { if (r.data && r.data.name) setInvoiceCustomer(prev => ({...prev, name: r.data.name, email: r.data.email || prev.email, id_type: r.data.id_type || prev.id_type})); })
                            .catch(() => {});
                        }
                      }}
                      className="px-2 py-1.5 rounded border text-xs" style={{ borderColor: 'var(--border-light)' }} data-testid="invoice-customer-id" />
                  </div>
                  <input type="text" placeholder="Nombre / Razón Social" value={invoiceCustomer.name}
                    onChange={e => setInvoiceCustomer({...invoiceCustomer, name: e.target.value})}
                    className="w-full px-2 py-1.5 rounded border text-xs" style={{ borderColor: 'var(--border-light)' }} data-testid="invoice-customer-name" />
                  <input type="email" placeholder="Email (para enviar factura)" value={invoiceCustomer.email}
                    onChange={e => setInvoiceCustomer({...invoiceCustomer, email: e.target.value})}
                    className="w-full px-2 py-1.5 rounded border text-xs" style={{ borderColor: 'var(--border-light)' }} data-testid="invoice-customer-email" />
                </div>
              )}
              <div className="flex justify-between items-center">
                <span className="font-medium">Método de pago:</span>
                <span>{PAYMENT_LABELS[paymentMethod]}</span>
              </div>
              {selectedCustomer && (
                <div className="flex justify-between items-center mt-1">
                  <span className="font-medium text-sm">Cliente:</span>
                  <span className="text-sm text-accent-text font-medium">{selectedCustomer.name}</span>
                </div>
              )}
              <div className="flex justify-between items-center mt-2">
                <span className="text-lg font-bold">TOTAL:</span>
                <span className="text-2xl font-bold text-accent-text">${cartTotal.toFixed(2)}</span>
              </div>
            </div>
            <div className="flex gap-3">
              <button onClick={() => setShowConfirm(false)} disabled={processing} className="btn-secondary flex-1">Cancelar</button>
              <button onClick={confirmSale} disabled={processing} className="btn-primary flex-1">
                {processing ? 'Procesando...' : 'Confirmar Venta'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Quick Create Customer Modal */}
      {showQuickCustomer && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[60] p-4">
          <div className="bg-white rounded-xl max-w-sm w-full p-5" data-testid="quick-customer-modal">
            <h3 className="font-bold text-lg mb-3">Nuevo Cliente Rapido</h3>
            <div className="space-y-3">
              <input type="text" value={quickCustomerName} onChange={e => setQuickCustomerName(e.target.value)} autoFocus
                placeholder="Nombre *" className="w-full px-4 py-2.5 rounded-lg border" style={{ borderColor: 'var(--border-light)' }}
                onKeyDown={e => e.key === 'Enter' && createQuickCustomer()} data-testid="quick-customer-name" />
              <input type="text" value={quickCustomerPhone} onChange={e => setQuickCustomerPhone(e.target.value)}
                placeholder="Telefono (opcional)" className="w-full px-4 py-2.5 rounded-lg border" style={{ borderColor: 'var(--border-light)' }}
                onKeyDown={e => e.key === 'Enter' && createQuickCustomer()} data-testid="quick-customer-phone" />
              <div className="flex gap-3">
                <button onClick={() => setShowQuickCustomer(false)} className="btn-secondary flex-1">Cancelar</button>
                <button onClick={createQuickCustomer} className="btn-primary flex-1">Crear</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Receipt Modal (after sale) */}
      {showReceipt && lastSale && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-sm w-full p-6 text-center" data-testid="receipt-modal">
            <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: 'var(--accent-wash)' }}>
              <Check size={32} className="text-accent-text" />
            </div>
            <h3 className="text-xl font-bold mb-2">Venta Exitosa</h3>
            <p className="text-2xl font-bold text-accent-text mb-4">${lastSale.total.toFixed(2)}</p>
            {lastSale.invoice_type === 'invoice' && lastSale.access_key && (
              <div className="mb-4 p-3 rounded-lg bg-green-50 border border-green-200 text-left">
                <p className="text-xs font-bold text-green-800 mb-1">FACTURA ELECTRONICA AUTORIZADA</p>
                <p className="text-xs text-green-700 font-mono break-all">{lastSale.access_key}</p>
              </div>
            )}
            <div className="flex gap-3">
              <button onClick={() => printReceipt(lastSale)}
                className="flex-1 py-3 rounded-lg font-medium text-sm flex items-center justify-center gap-2"
                style={{ background: 'var(--accent-primary)', color: 'white' }}
                data-testid="print-receipt-btn">
                <Printer size={18} />Imprimir
              </button>
              {lastSale.factuplan_id && (
                <button onClick={() => window.open(`${API}/factuplan/receipt/${lastSale.factuplan_id}/pdf`, '_blank')}
                  className="py-3 px-4 rounded-lg font-medium text-sm flex items-center justify-center gap-2 border border-green-300 text-green-700 hover:bg-green-50"
                  data-testid="download-ride-btn">
                  <FileText size={18} />RIDE PDF
                </button>
              )}
              <button onClick={() => setShowReceipt(false)}
                className="flex-1 py-3 rounded-lg font-medium text-sm border"
                style={{ borderColor: 'var(--border-light)' }}
                data-testid="close-receipt-btn">
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Day Sales Detail Modal */}
      {showDaySales && (
        <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setShowDaySales(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col" data-testid="day-sales-modal">
              <div className="p-6 border-b" style={{ borderColor: 'var(--border-light)', background: 'var(--accent-wash)' }}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <ClipboardList size={28} className="text-accent-text" />
                    <div>
                      <h2 className="text-xl font-bold">Ventas del Día</h2>
                      <p className="text-sm text-text-muted">{new Date().toLocaleDateString('es-EC', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}</p>
                    </div>
                  </div>
                  <button onClick={() => setShowDaySales(false)} className="p-2 hover:bg-white rounded-lg"><X size={20} /></button>
                </div>
              </div>
              <div className="flex-1 overflow-y-auto p-6">
                {daySalesData.length === 0 ? (
                  <div className="text-center py-12">
                    <Receipt size={48} className="mx-auto text-text-muted mb-4 opacity-50" />
                    <p className="text-text-muted">No hay ventas registradas hoy</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {daySalesData.map((sale, idx) => (
                      <div key={sale.id || idx} className="border rounded-xl overflow-hidden" style={{ borderColor: 'var(--border-light)' }}>
                        <div className="flex items-center justify-between p-4 bg-gray-50">
                          <div className="flex items-center gap-3">
                            <span className="w-8 h-8 rounded-full bg-accent-wash text-accent-text flex items-center justify-center font-bold text-sm">
                              {idx + 1}
                            </span>
                            <div>
                              <p className="font-medium text-sm">
                                {new Date((sale.created_at || sale.timestamp || '').toString().endsWith('Z') ? sale.created_at || sale.timestamp : (sale.created_at || sale.timestamp) + 'Z').toLocaleTimeString('es-EC', { hour: '2-digit', minute: '2-digit', timeZone: 'America/Guayaquil' })}
                                {sale.cashier_name && <span className="text-text-muted ml-2">- {sale.cashier_name}</span>}
                              </p>
                              <p className="text-xs text-text-muted">
                                {PAYMENT_LABELS[sale.payment_method] || sale.payment_method} - {sale.items?.length || 0} productos
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="font-bold text-lg text-accent-text">${(sale.total || 0).toFixed(2)}</span>
                            <button onClick={() => printReceipt(sale)}
                              className="p-2 hover:bg-white rounded-lg transition-colors" title="Imprimir recibo"
                              data-testid={`print-sale-${idx}`}>
                              <Printer size={18} className="text-text-muted" />
                            </button>
                            {hasPermission(['admin']) && (
                              <button onClick={() => handleVoidSale(sale.id, sale.total || 0)}
                                disabled={voidingId === sale.id}
                                className="p-2 hover:bg-red-50 rounded-lg transition-colors" title="Anular venta"
                                data-testid={`void-sale-${idx}`}>
                                {voidingId === sale.id 
                                  ? <span className="animate-spin inline-block w-4 h-4 border-2 border-red-300 border-t-red-600 rounded-full" />
                                  : <Trash2 size={16} className="text-red-400" />
                                }
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="p-4">
                          <table className="w-full text-sm">
                            <thead>
                              <tr className="text-text-muted text-xs">
                                <th className="text-left pb-2">Producto</th>
                                <th className="text-center pb-2">Cant.</th>
                                <th className="text-right pb-2">P.Unit.</th>
                                <th className="text-right pb-2">Subtotal</th>
                              </tr>
                            </thead>
                            <tbody>
                              {(sale.items || []).map((item, j) => (
                                <tr key={j} className="border-t" style={{ borderColor: 'var(--border-light)' }}>
                                  <td className="py-2 text-left">{item.product_name}</td>
                                  <td className="py-2 text-center">{item.quantity}</td>
                                  <td className="py-2 text-right">${(item.unit_price || 0).toFixed(2)}</td>
                                  <td className="py-2 text-right font-medium">${(item.subtotal || (item.quantity * item.unit_price)).toFixed(2)}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              <div className="p-4 border-t" style={{ borderColor: 'var(--border-light)' }}>
                <p className="text-center text-sm text-text-muted">
                  Total: <strong className="text-accent-text">{daySalesData.length}</strong> ventas
                </p>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Cash Close Modal */}
      {showCashClose && cashCloseData && (
        <>
          <div className="fixed inset-0 bg-black/50 z-50" onClick={() => setShowCashClose(false)} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto" data-testid="cash-close-modal">
              <div className="p-6 border-b text-center" style={{ borderColor: 'var(--border-light)', background: '#dc2626' }}>
                <Receipt size={32} className="mx-auto text-white mb-2" />
                <h2 className="text-xl font-bold text-white">Cierre de Caja</h2>
                <p className="text-red-100 text-sm">{cashCloseData.date ? new Date(cashCloseData.date + 'T12:00:00').toLocaleDateString('es-EC', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }) : ''}</p>
                <div className="flex items-center justify-center gap-2 mt-3">
                  <input type="date" value={cashCloseDate || cashCloseData.date || ''}
                    onChange={e => { setCashCloseDate(e.target.value); handleCashClose(e.target.value); }}
                    className="px-3 py-1.5 rounded-lg text-sm text-gray-800 border-0"
                    data-testid="cash-close-date-picker" />
                </div>
              </div>
              <div className="p-6 space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <div className="bg-green-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-green-600 font-medium">Ingresos</p>
                    <p className="text-xl font-bold text-green-700" data-testid="cc-revenue">${cashCloseData.total_revenue.toFixed(2)}</p>
                  </div>
                  <div className="bg-blue-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-blue-600 font-medium">Ganancia</p>
                    <p className="text-xl font-bold text-blue-700" data-testid="cc-profit">${cashCloseData.total_profit.toFixed(2)}</p>
                  </div>
                  <div className="bg-red-50 rounded-lg p-3 text-center">
                    <p className="text-xs text-red-600 font-medium">Costo</p>
                    <p className="text-xl font-bold text-red-700" data-testid="cc-cost">${cashCloseData.total_cost.toFixed(2)}</p>
                  </div>
                </div>
                <p className="text-center text-text-muted text-sm">Total de ventas: <strong>{cashCloseData.total_sales}</strong></p>
                
                {/* IVA Breakdown */}
                {cashCloseData.iva_regime === 'iva_15' && (
                  <div className="bg-amber-50 rounded-lg p-3 border border-amber-200">
                    <h3 className="font-semibold text-sm mb-2 text-amber-800">Desglose IVA</h3>
                    <div className="space-y-1 text-sm">
                      <div className="flex justify-between"><span className="text-amber-700">Subtotal (Base 15%):</span><span className="font-bold text-amber-800">${cashCloseData.subtotal_base_iva?.toFixed(2)}</span></div>
                      <div className="flex justify-between"><span className="text-amber-700">IVA 15%:</span><span className="font-bold text-amber-800">${cashCloseData.total_iva?.toFixed(2)}</span></div>
                      <div className="flex justify-between border-t border-amber-300 pt-1 mt-1"><span className="font-bold text-amber-800">Total (IVA incluido):</span><span className="font-bold text-amber-900">${cashCloseData.total_revenue?.toFixed(2)}</span></div>
                    </div>
                  </div>
                )}
                {cashCloseData.iva_regime !== 'iva_15' && (
                  <div className="bg-gray-50 rounded-lg p-3 border" style={{ borderColor: 'var(--border-light)' }}>
                    <div className="flex justify-between text-sm">
                      <span className="text-text-muted">Base IVA 0%:</span><span className="font-bold">${cashCloseData.total_revenue?.toFixed(2)}</span>
                    </div>
                  </div>
                )}
                
                {/* Products Sold Summary */}
                {cashCloseData.products_sold && cashCloseData.products_sold.length > 0 && (
                  <div>
                    <h3 className="font-semibold text-sm mb-2">Productos Vendidos</h3>
                    <div className="border rounded-lg overflow-hidden" style={{ borderColor: 'var(--border-light)' }}>
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="text-left px-3 py-2 text-xs font-semibold text-text-muted">Producto</th>
                            <th className="text-center px-2 py-2 text-xs font-semibold text-text-muted">Cant</th>
                            <th className="text-right px-3 py-2 text-xs font-semibold text-text-muted">Total</th>
                          </tr>
                        </thead>
                        <tbody>
                          {cashCloseData.products_sold.map((p, idx) => (
                            <tr key={idx} className={idx % 2 === 0 ? '' : 'bg-gray-50'}>
                              <td className="px-3 py-2 text-sm font-medium truncate max-w-[200px]">{p.product_name}</td>
                              <td className="px-2 py-2 text-sm text-center font-bold">{p.quantity}</td>
                              <td className="px-3 py-2 text-sm text-right font-semibold text-accent-text">${p.subtotal.toFixed(2)}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                <div>
                  <h3 className="font-semibold text-sm mb-3">Desglose por Método de Pago</h3>
                  <div className="space-y-2">
                    {[{key:'cash',icon:Banknote,color:'text-green-600'},{key:'card',icon:CreditCard,color:'text-blue-600'},{key:'transfer',icon:DollarSign,color:'text-purple-600'}].map(m => (
                      <div key={m.key} className="flex items-center justify-between p-3 rounded-lg border" style={{ borderColor: 'var(--border-light)' }}>
                        <div className="flex items-center gap-2"><m.icon size={20} className={m.color} /><span className="font-medium text-sm">{PAYMENT_LABELS[m.key]}</span></div>
                        <div className="text-right">
                          <p className="font-bold">${(cashCloseData.by_payment_method?.[m.key]?.total || 0).toFixed(2)}</p>
                          <p className="text-xs text-text-muted">{cashCloseData.by_payment_method?.[m.key]?.count || 0} ventas</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="flex gap-3">
                  <button onClick={() => printCashClose(cashCloseData)} className="btn-secondary flex-1 flex items-center justify-center gap-2" data-testid="cc-print-btn">
                    <Printer size={16} /> Imprimir
                  </button>
                  <button onClick={() => setShowCashClose(false)} className="btn-primary flex-1" data-testid="cc-close-btn">Cerrar</button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Weight Input Modal */}
      {weightInput.show && weightInput.product && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 w-full max-w-sm">
            <h3 className="font-bold text-lg mb-1">{weightInput.product.name}</h3>
            <p className="text-sm text-text-muted mb-4">
              ${weightInput.product.price.toFixed(2)} / {UNIT_SHORT[weightInput.product.unit_type] || 'ud'} — Stock: {weightInput.product.stock} {UNIT_SHORT[weightInput.product.unit_type] || 'ud'}
            </p>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-2">
                Cantidad ({weightInput.product.unit_type === 'libra' ? 'libras' : weightInput.product.unit_type === 'kilo' ? 'kilos' : 'gramos'})
              </label>
              <input type="number" step="0.01" min="0.01" autoFocus
                value={weightInput.value}
                onChange={(e) => setWeightInput({ ...weightInput, value: e.target.value })}
                onKeyDown={(e) => e.key === 'Enter' && confirmWeightAdd()}
                className="w-full px-4 py-3 rounded-lg border text-center text-2xl font-bold"
                style={{ borderColor: 'var(--accent-primary)' }}
                placeholder="0.00"
                data-testid="weight-modal-input" />
              {weightInput.value && parseFloat(weightInput.value) > 0 && (
                <p className="text-center mt-2 text-lg font-semibold text-accent-text">
                  Subtotal: ${(parseFloat(weightInput.value) * weightInput.product.price).toFixed(2)}
                </p>
              )}
            </div>
            <div className="flex gap-3">
              <button onClick={() => setWeightInput({ show: false, product: null, value: '' })}
                className="flex-1 px-4 py-3 rounded-lg border font-medium" style={{ borderColor: 'var(--border-light)' }}
                data-testid="weight-modal-cancel">
                Cancelar
              </button>
              <button onClick={confirmWeightAdd}
                className="flex-1 btn-primary py-3" data-testid="weight-modal-confirm">
                <Check size={18} className="inline mr-1" />
                Agregar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Barcode Scanner */}
      {showScanner && (
        <BarcodeScanner
          onScan={handleBarcodeScan}
          onClose={() => setShowScanner(false)}
        />
      )}

      {/* POS Sync Modal */}
      {showSyncModal && (
        <PosSyncModal
          onClose={() => setShowSyncModal(false)}
          onConnect={connectSync}
          getAuthHeader={getAuthHeader}
        />
      )}

      {/* Support Chat */}
      {showSupportChat && (
        <SupportChat onClose={() => { setShowSupportChat(false); loadNotifications(); }} />
      )}

      {/* Mobile floating notification badges */}
      {!showSupportChat && (notifications.total > 0 || supportUnread > 0) && (
        <div className="fixed bottom-16 right-4 z-40 flex flex-col gap-2 md:hidden" data-testid="pos-mobile-notifs">
          {supportUnread > 0 && (
            <button onClick={() => setShowSupportChat(true)}
              className="w-11 h-11 rounded-full shadow-lg flex items-center justify-center text-white relative"
              style={{ background: '#6366f1' }}>
              <Headphones size={20} />
              <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">{supportUnread}</span>
            </button>
          )}
          {notifications.unread_chats > 0 && (
            <button onClick={() => navigate(`${basePath}/chat`)}
              className="w-11 h-11 rounded-full shadow-lg flex items-center justify-center text-white relative"
              style={{ background: '#3b82f6' }}>
              <MessageSquare size={20} />
              <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">{notifications.unread_chats}</span>
            </button>
          )}
          {notifications.pending_orders > 0 && (
            <button onClick={() => navigate(`${basePath}/orders`)}
              className="w-11 h-11 rounded-full shadow-lg flex items-center justify-center text-white relative"
              style={{ background: '#f97316' }}>
              <Package size={20} />
              <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center">{notifications.pending_orders}</span>
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default PointOfSale;
